import * as React from 'react';

import { cn } from '@heartplus/ui';

import { getInjectedSelectComponents } from '../../custom-blocks/dropdown-block';
import { parseDropdownTokenOptions } from './options';
import {
  DEFAULT_DROPDOWN_TOKEN_CLASS_NAME,
  INLINE_TOKEN_CLASS,
  INLINE_TOKEN_DROPDOWN_WIDTH,
} from './styles';

type EditableDropdownTokenProps = {
  value: string;
  label: string;
  optionsValue: string;
  onValueChange: (nextValue: string) => void;
};

export const EditableDropdownToken: React.FC<EditableDropdownTokenProps> = ({
  value,
  label,
  optionsValue,
  onValueChange,
}) => {
  const options = React.useMemo(
    () => parseDropdownTokenOptions(optionsValue),
    [optionsValue],
  );
  const selectedOption = options.find((option) => option.value === value);
  const optionClassName =
    selectedOption?.className || DEFAULT_DROPDOWN_TOKEN_CLASS_NAME;
  const displayPlaceholder = label || 'Select…';
  const selectComponents = getInjectedSelectComponents();

  if (selectComponents) {
    const { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } =
      selectComponents;

    return (
      <span
        contentEditable={false}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        className="inline-flex items-center"
      >
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger
            className={cn(
              INLINE_TOKEN_CLASS,
              'justify-between',
              INLINE_TOKEN_DROPDOWN_WIDTH,
              optionClassName,
            )}
          >
            <SelectValue placeholder={displayPlaceholder} />
          </SelectTrigger>
          <SelectContent
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            {options.length === 0 ? (
              <SelectItem value="" disabled>
                {displayPlaceholder}
              </SelectItem>
            ) : (
              options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </span>
    );
  }

  return (
    <select
      contentEditable={false}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      className={cn(
        INLINE_TOKEN_CLASS,
        'border',
        INLINE_TOKEN_DROPDOWN_WIDTH,
        optionClassName,
      )}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
    >
      <option value="" disabled>
        {displayPlaceholder}
      </option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};
